import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const BenefitsSection = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: 'UserCheck',
      title: 'Personalized Counseling',
      description: 'Get one-on-one guidance from expert counselors who understand your goals, interests, and academic background.',
      features: ['Career path guidance', 'Course recommendations', 'Admission assistance'],
      gradient: 'from-cyan-500 to-blue-500',
      bgColor: 'from-cyan-50 to-blue-50'
    },
    {
      icon: 'Building',
      title: 'Virtual Campus Tours',
      description: 'Experience campuses like never before with immersive 360° tours that let you explore every corner virtually.',
      features: ['Interactive hotspots', 'Facility walkthroughs', 'Student life glimpses'],
      gradient: 'from-violet-500 to-purple-500',
      bgColor: 'from-violet-50 to-purple-50'
    },
    {
      icon: 'MessageCircle',
      title: 'Instant Chat Support',
      description: 'Connect directly with institute representatives and get immediate answers to all your questions.',
      features: ['Real-time responses', 'Document sharing', 'Appointment scheduling'],
      gradient: 'from-pink-500 to-rose-500',
      bgColor: 'from-pink-50 to-rose-50'
    },
    {
      icon: 'FileText',
      title: 'Downloadable Resources',
      description: 'Access comprehensive information packages including brochures, forms, and detailed course catalogs.',
      features: ['Course catalogs', 'Admission forms', 'Fee structures'],
      gradient: 'from-amber-500 to-orange-500',
      bgColor: 'from-amber-50 to-orange-50'
    }
  ];

  return (
    <section className="py-12 md:py-16 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-5 py-2 rounded-full mb-4 shadow-md">
            <Icon name="Star" size={16} color="white" />
            <span className="text-sm font-medium">Student Benefits</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
            Everything You Need to Make the Right Choice
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            StudyExpo provides comprehensive tools and resources to help you discover, evaluate, and connect with your ideal educational institution.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {benefits?.map((benefit, index) => (
            <div 
              key={index}
              className={`bg-gradient-to-br ${benefit?.bgColor} border-2 border-white rounded-2xl p-8 hover:shadow-2xl transition-all hover:-translate-y-2`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-16 h-16 bg-gradient-to-br ${benefit?.gradient} rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                  <Icon name={benefit?.icon} size={28} color="white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                    {benefit?.title}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {benefit?.description}
                  </p>
                  <ul className="space-y-2">
                    {benefit?.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-foreground font-medium">
                        <div className={`w-2 h-2 bg-gradient-to-r ${benefit?.gradient} rounded-full`} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Success Stories */}
        <div className="bg-gradient-to-r from-cyan-500 via-violet-500 to-pink-500 rounded-2xl p-8 md:p-12 text-white shadow-2xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-heading font-bold mb-4">
                Join Thousands of Successful Students
              </h3>
              <p className="text-white/95 mb-6 text-lg">
                Over 50,000 students have found their perfect educational match through StudyExpo. Start your journey today and discover opportunities that align with your dreams.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="bg-white text-violet-600 hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all font-semibold"
                  onClick={() => navigate('/student-registration')}
                  iconName="UserPlus"
                  iconPosition="left"
                >
                  Register Now
                </Button>
                <Button 
                  size="lg" 
                  className="bg-white/20 backdrop-blur-sm border-2 border-white text-white hover:bg-white/30 shadow-lg"
                  onClick={() => navigate('/exhibition-hall')}
                  iconName="Building"
                  iconPosition="left"
                >
                  Explore Fair
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/20 backdrop-blur-md rounded-2xl p-6 border-2 border-white/30 hover:bg-white/25 transition-all">
                <Icon name="Users" size={32} color="white" className="mb-3" />
                <p className="text-3xl font-bold mb-1">5K+</p>
                <p className="text-sm text-white/90">Registered Students</p>
              </div>
              <div className="bg-white/20 backdrop-blur-md rounded-2xl p-6 border-2 border-white/30 hover:bg-white/25 transition-all">
                <Icon name="ThumbsUp" size={32} color="white" className="mb-3" />
                <p className="text-3xl font-bold mb-1">98%</p>
                <p className="text-sm text-white/90">Satisfaction Rate</p>
              </div>
              <div className="bg-white/20 backdrop-blur-md rounded-2xl p-6 border-2 border-white/30 hover:bg-white/25 transition-all">
                <Icon name="Award" size={32} color="white" className="mb-3" />
                <p className="text-3xl font-bold mb-1">Top Institutes</p>
                <p className="text-sm text-white/90">Scholarships Found</p>
              </div>
              <div className="bg-white/20 backdrop-blur-md rounded-2xl p-6 border-2 border-white/30 hover:bg-white/25 transition-all">
                <Icon name="Clock" size={32} color="white" className="mb-3" />
                <p className="text-3xl font-bold mb-1">24/7</p>
                <p className="text-sm text-white/90">Support Available</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;